/**
 * A small palette of project/entity avatar icons + accent colors, used to give
 * listing rows a colorful icon tile (matching the design).
 *
 * The icon names are Material Icons ligatures — this module stays free of any
 * Angular/Material dependency; the consumer renders them (e.g. with `mat-icon`).
 */

/**
 * Curated Material Icons ligatures that read as "project / work" glyphs. Chosen
 * to approximate the design's category icons (design, dev, analytics, research,
 * marketing, ops) plus a few extras for variety.
 */
export const PROJECT_ICONS: readonly string[] = [
  'palette',
  'code',
  'analytics',
  'science',
  'campaign',
  'settings',
  'design_services',
  'rocket_launch',
  'insights',
  'language',
  'draw',
  'hub',
];

/** Six stock accent colors for avatar tiles (the design's category hues). */
export const PROJECT_ICON_COLORS: readonly string[] = [
  '#9b5de5', // purple
  '#3b82f6', // blue
  '#06b6d4', // cyan
  '#10b981', // green
  '#f59e0b', // amber
  '#6b7280', // gray
];

/** A resolved avatar: a Material icon ligature + an accent color. */
export interface ProjectIcon {
  icon: string;
  color: string;
}

/** Stable 32-bit hash of a string from a seed (so distinct seeds decorrelate). */
function hash(key: string, seed: number): number {
  let value = seed >>> 0;
  for (const character of key) {
    value = (value * 31 + character.charCodeAt(0)) >>> 0;
  }
  return value;
}

/**
 * Deterministically picks an icon + color for a key (e.g. a round id), so the
 * same entity always renders the same avatar. The icon and color use different
 * hash seeds so they vary independently (even for short, similar keys).
 */
export function pickProjectIcon(key: string): ProjectIcon {
  return {
    icon: PROJECT_ICONS[hash(key, 17) % PROJECT_ICONS.length],
    color: PROJECT_ICON_COLORS[hash(key, 131) % PROJECT_ICON_COLORS.length],
  };
}